/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, useMemo, useCallback, useContext } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { Cell, Column } from 'react-table'
import { readEnergyEventsRequest } from './read-energy'
import type { HomeId } from '@racepointenergy/swc/scs/types'

import { useAppDispatch } from 'app/hooks'
import type { Message } from 'features/home/types'
import { Table, TableLoader } from 'components/Table'
import { messages } from '../messages'
import {
  MessageCenterWrapper,
  MessageCenterHeader,
  MessageCenterRowStyle,
} from './styles'
import { DetailsCell, MessageCell, TimeCell } from './cells'
import { HomeStateContext } from 'features/home/homeStateContext'
import { MessageCenterBlock } from './MessageCenterBlock'

interface MessageCenterProps {
  homeId: HomeId
}

function MessageCenter({ homeId }: MessageCenterProps) {
  const { formatMessage } = useIntl()
  const { isOnline } = useContext(HomeStateContext)
  const dispatch = useAppDispatch()
  const [energyMessages, setEnergyMessages] = useState<Message[]>([])
  const [isFetching, setIsFetching] = useState(true)

  const asyncGetEnergyMessages = useCallback(async () => {
    const request = dispatch(
      readEnergyEventsRequest.initiate({
        homeId,
      })
    )

    await request
      .unwrap()
      .then((result) => {
        setIsFetching(false)
        if (result?.entities) {
          //@ts-ignore
          const msgs = Object.values(result.entities).reverse()
          const filteredMsgs = msgs.filter(
            (msg) => msg?.eventName !== 'TopConsumersSoFar'
          )
          //@ts-ignore
          setEnergyMessages(filteredMsgs)
        }
      })
      .catch((err) => {
        setIsFetching(false)
        console.log(err)
      })
  }, [dispatch, homeId])

  useEffect(() => {
    asyncGetEnergyMessages()
  }, [asyncGetEnergyMessages])

  const headers = useMemo(
    () => ({
      event: {
        label: formatMessage(messages.headerEvent),
        id: 'Event',
      },
      date: {
        label: formatMessage(messages.headerDate),
        id: 'Date',
      },
      details: {
        label: formatMessage(messages.headerDetails),
        id: 'Details',
      },
    }),
    [formatMessage]
  )

  const columns: readonly Column<Message>[] = useMemo(
    () => [
      {
        id: headers.event.id,
        Header: headers.event.label,
        accessor: (row) => row.eventName,
        Cell: MessageCell,
        percentWidth: '30%',
      },
      {
        id: headers.date.id,
        Header: headers.date.label,
        accessor: (row) => row.eventUnixTime,
        Cell: TimeCell,
        percentWidth: '25%',
      },
      {
        id: headers.details.id,
        Header: headers.details.label,
        accessor: (row) => row.eventDetails,
        Cell: DetailsCell,
        percentWidth: '45%',
      },
    ],
    [headers]
  )

  return (
    <MessageCenterWrapper>
      <MessageCenterHeader>
        <FormattedMessage {...messages.messageCenter} />
      </MessageCenterHeader>
      <div>
        {isFetching && isOnline && <TableLoader />}

        {(!isFetching || !isOnline) && (
          <div>
            <Table
              columns={columns}
              rowStyles={MessageCenterRowStyle}
              data={energyMessages}
              mobileViewComponent={MessageCenterBlock}
            />
          </div>
        )}
      </div>
    </MessageCenterWrapper>
  )
}

export default MessageCenter
